import React from "react";
import dayjs from "dayjs";
import {
    Box,
    Text,
    IconButton,
    Tooltip,
    Tag,
    Icon
} from "@chakra-ui/react";
import { MdContentCopy, MdCheckCircle } from "react-icons/md";
import { copy } from "../../../../tools/tools";

/**
 * Returns Chakra style props for the contact table row container.
 * @param {number} index - Row index in the list
 * @param {number} total - Total number of rows
 * @returns {Object} Chakra style props
 */
function rowContainerSx(index, total) {
    return {
        display: "grid",
        gridTemplateColumns: "2fr 1.5fr 1.5fr 2fr",
        alignItems: "center",
        borderBottom: index !== total - 1 ? "1px solid rgba(255, 255, 255, 0.08)" : "none",
        transition: "all 0.3s ease",
        cursor: "pointer",
        _hover: { background: "rgba(255, 255, 255, 0.06)", backdropFilter: "blur(10px)" }
    };
}

/**
 * @param {Object} props
 * @param {Object} props.contact - Single contact card object
 * @param {number} props.index - Row index
 * @param {number} props.total - Total number of contacts
 * @param {string} props.nameField - Reservation field for name
 * @param {boolean} props.isMobile - Mobile flag
 * @param {number|null} props.refIndexCopied - Currently copied ref ID
 * @param {Function} props.setRefIndexCopied - Setter for copied ref
 * @param {string} props.currencySymbol - Resolved currency symbol
 * @param {Function} props.onRowClick - Click handler
 * @returns {React.ReactElement}
 */
function ContactTableRow({ contact, index, total, nameField, isMobile, refIndexCopied, setRefIndexCopied, currencySymbol, onRowClick }) {
    const cellPadding = isMobile ? "14px" : "16px 18px";

    return (
        <Box onClick={() => onRowClick(contact)} {...rowContainerSx(index, total)}>
            <Box padding={cellPadding}>
                <Text color="rgba(255, 255, 255, 0.9)" fontSize={isMobile ? "0.75rem" : "0.85rem"} fontWeight={500}>
                    {contact?.reservation?.[nameField] || "N/A"}
                </Text>
            </Box>
            <Box padding={cellPadding}>
                <Text color="rgba(255, 255, 255, 0.7)" fontSize={isMobile ? "0.7rem" : "0.75rem"}>
                    {dayjs(contact?.created_at).format("DD/MM/YYYY HH:mm")}
                </Text>
            </Box>
            <Box padding={cellPadding}>
                <AmountCell contact={contact} currencySymbol={currencySymbol} isMobile={isMobile} />
            </Box>
            <Box padding={cellPadding}>
                <ReferenceCell contact={contact} refIndexCopied={refIndexCopied} setRefIndexCopied={setRefIndexCopied} isMobile={isMobile} />
            </Box>
        </Box>
    );
}

/** Chakra style props for the "Free" tag displayed when the amount is zero. */
const freeChipSx = (isMobile) => ({
    background: "rgba(76, 175, 80, 0.15)",
    backdropFilter: "blur(10px)",
    color: "rgba(76, 175, 80, 1)",
    border: "1px solid rgba(76, 175, 80, 0.4)",
    fontWeight: 600,
    fontSize: isMobile ? "0.65rem" : "0.7rem",
    height: isMobile ? "22px" : "24px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(76, 175, 80, 0.15)"
});

/**
 * Renders the amount with currency symbol or a "Free" tag.
 * @param {Object} props
 * @param {Object} props.contact - Contact card with amount
 * @param {string} props.currencySymbol - Currency symbol
 * @param {boolean} props.isMobile - Mobile flag
 * @returns {React.ReactElement}
 */
function AmountCell({ contact, currencySymbol, isMobile }) {
    if (!contact?.amount) {
        return <Tag size="sm" {...freeChipSx(isMobile)}>Free</Tag>;
    }

    return (
        <Text color="rgba(255, 255, 255, 0.95)" fontSize={isMobile ? "0.8rem" : "0.85rem"} fontWeight={600}>
            {contact.amount}
            <AmountCurrencyIndicator currencySymbol={currencySymbol} />
        </Text>
    );
}

/**
 * Renders the currency symbol or a fallback tooltip indicator.
 * @param {Object} props
 * @param {string} props.currencySymbol - Currency symbol or null
 * @returns {React.ReactElement}
 */
function AmountCurrencyIndicator({ currencySymbol }) {
    if (currencySymbol) {
        return (
            <Box as="span" ml={1} color="rgba(255, 255, 255, 0.95)">
                {currencySymbol}
            </Box>
        );
    }

    return (
        <Tooltip label="Currency unavailable" hasArrow>
            <Box as="span" ml={1} color="rgba(255, 152, 0, 0.8)" fontSize="0.7rem" fontStyle="italic">
                (?)
            </Box>
        </Tooltip>
    );
}

/**
 * Returns Chakra style props for the copy reference icon button.
 * @param {boolean} isCopied - Whether the reference was recently copied
 * @returns {Object} Chakra style props
 */
function copyButtonSx(isCopied) {
    return {
        color: isCopied ? "rgba(76, 175, 80, 1)" : "rgba(255, 255, 255, 0.6)",
        padding: "6px",
        background: "rgba(255, 255, 255, 0.05)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        transition: "all 0.3s ease",
        _hover: { background: "rgba(255, 255, 255, 0.1)", border: "1px solid rgba(255, 255, 255, 0.15)" }
    };
}

/**
 * Renders the payment reference with a copy-to-clipboard button.
 * @param {Object} props
 * @param {Object} props.contact - Contact card with payment reference
 * @param {number|null} props.refIndexCopied - ID of last copied ref
 * @param {Function} props.setRefIndexCopied - Setter for copied ref state
 * @param {boolean} props.isMobile - Mobile flag
 * @returns {React.ReactElement}
 */
function ReferenceCell({ contact, refIndexCopied, setRefIndexCopied, isMobile }) {
    if (!contact?.payment) {
        return (
            <Text color="rgba(255, 255, 255, 0.5)" fontSize={isMobile ? "0.7rem" : "0.75rem"} fontStyle="italic">
                No reference
            </Text>
        );
    }

    const isCopied = refIndexCopied === contact.id;

    return (
        <Box display="flex" alignItems="center" gap={2}>
            <Text color="rgba(255, 255, 255, 0.8)" fontSize={isMobile ? "0.7rem" : "0.75rem"} fontFamily="monospace" letterSpacing="0.3px">
                {contact.payment.reference?.substring(0, 9) + "****"}
            </Text>
            <Tooltip label={isCopied ? "Copied!" : "Copy reference"} hasArrow>
                <IconButton
                    aria-label="Copy reference"
                    size="sm"
                    minW="auto"
                    h="auto"
                    onClick={(event) => { event.stopPropagation(); copy(contact.id, contact.payment.reference, setRefIndexCopied); }}
                    {...copyButtonSx(isCopied)}
                    icon={<Icon as={isCopied ? MdCheckCircle : MdContentCopy} boxSize="16px" />}
                />
            </Tooltip>
        </Box>
    );
}

export default ContactTableRow;
